import React, { useState, useEffect } from "react";
import hosys from "@/api/api";
import { useVenue } from "@/components/shared/VenueContext";
import { usePlan, PLAN_LABELS, STATUS_LABELS, PLAN_FEATURES } from "@/components/shared/PlanGuard";
import PageHeader from "@/components/shared/PageHeader";
import QuickLoader from "@/components/shared/QuickLoader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { resolveFileUrl } from "@/utils";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Building2, Save, Loader2, CheckCircle2, Upload } from "lucide-react";
import moment from "moment";

const VENUE_TYPES = {
  restaurant: "Restaurant", bar: "Bar", cafeteria: "Cafeteria",
  takeaway: "Take Away", rostisseria: "Rostisseria",
  hotel_cuina: "Hotel amb Cuina", pastisseria: "Pastisseria / Forn", other: "Altres",
};

const STATUS_COLORS = {
  active: "bg-emerald-100 text-emerald-700",
  trial: "bg-blue-100 text-blue-700",
  suspended: "bg-red-100 text-red-700",
  inactive: "bg-gray-100 text-gray-500",
};

const PLAN_COLORS = {
  basic: "bg-gray-100 text-gray-700",
  professional: "bg-purple-100 text-purple-700",
  premium: "bg-amber-100 text-amber-700",
};

const FEATURE_LABELS = {
  time_control: "Control horari", temperatures: "Temperatures", cleaning: "Neteja",
  incidents: "Incidències", documents: "Documents", downloads: "Descàrregues",
  alerts: "Centre d'alertes", verifications: "Verificacions APPCC",
  compliance_dashboard: "Quadre de compliment", recipe_sheets: "Fitxes tècniques",
};

export default function VenueProfile() {
  const { currentVenue, userRole, loading: venueLoading, refreshData } = useVenue();
  const { subscription } = usePlan();
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);

  const isEditable = userRole === "superadmin" || userRole === "admin";

  useEffect(() => {
    if (currentVenue) {
      setForm({
        name: currentVenue.name || "",
        venue_type: currentVenue.venue_type || "restaurant",
        address: currentVenue.address || "",
        city: currentVenue.city || "",
        phone: currentVenue.phone || "",
        email: currentVenue.email || "",
        owner_email: currentVenue.owner_email || "",
        logo_url: currentVenue.logo_url || "",
        business_name: currentVenue.business_name || "",
        cif: currentVenue.cif || "",
        ccc_provincia: currentVenue.ccc_provincia || "",
        ccc_numero: currentVenue.ccc_numero || "",
      });
    }
  }, [currentVenue]);

  const handleSave = async () => {
    setSaving(true);
    await hosys.entities.Venue.update(currentVenue.id, form);
    await refreshData();
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLogoUploading(true);
    const { file_url } = await hosys.integrations.Core.UploadFile({ file });
    setForm(f => ({ ...f, logo_url: file_url }));
    setLogoUploading(false);
  };

  if (venueLoading) return <QuickLoader />;
  if (!currentVenue) return null;

  const plan = subscription?.plan || "basic";
  const status = subscription?.status || "trial";
  const features = PLAN_FEATURES[plan] || PLAN_FEATURES.basic;

  return (
    <div className="space-y-5 max-w-3xl">
      <PageHeader title="Perfil de l'Establiment" subtitle={currentVenue.name} />

      {/* Subscription banner */}
      <div className="flex flex-wrap gap-3 items-center p-4 bg-white rounded-2xl shadow-sm border-0">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">Pla:</span>
          <Badge className={`${PLAN_COLORS[plan]} border-0 font-semibold`}>{PLAN_LABELS[plan]}</Badge>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">Estat:</span>
          <Badge className={`${STATUS_COLORS[status]} border-0`}>{STATUS_LABELS[status]}</Badge>
        </div>
        {subscription?.renewal_date && (
          <span className="text-xs text-gray-400 ml-auto">
            Renovació: {moment(subscription.renewal_date).format("DD/MM/YYYY")}
          </span>
        )}
        {subscription?.start_date && (
          <span className="text-xs text-gray-400">
            Alta: {moment(subscription.start_date).format("DD/MM/YYYY")}
          </span>
        )}
      </div>

      {/* Profile form */}
      <Card className="p-6 border-0 shadow-sm">
        <h3 className="font-semibold text-gray-900 mb-4">Dades del local</h3>
        <div className="space-y-4">
          {/* Logo */}
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center overflow-hidden flex-shrink-0">
              {form.logo_url ? (
                <img src={resolveFileUrl(form.logo_url)} alt="Logo" className="w-full h-full object-cover" />
              ) : (
                <Building2 className="w-7 h-7 text-gray-400" />
              )}
            </div>
            {isEditable && (
              <div>
                <Label className="cursor-pointer">
                  <div className="flex items-center gap-2 text-sm text-[#7B2D3F] font-medium hover:underline cursor-pointer">
                    {logoUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                    {logoUploading ? "Pujant..." : "Pujar logotip"}
                  </div>
                  <input type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} disabled={logoUploading} />
                </Label>
                <p className="text-xs text-gray-400 mt-0.5">PNG, JPG fins a 5MB</p>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <Label>Nom comercial *</Label>
              <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} disabled={!isEditable} />
            </div>
            <div>
              <Label>Categoria</Label>
              <Select value={form.venue_type} onValueChange={v => setForm(f => ({ ...f, venue_type: v }))} disabled={!isEditable}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(VENUE_TYPES).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Telèfon</Label>
              <Input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} disabled={!isEditable} />
            </div>
            <div className="sm:col-span-2">
              <Label>Adreça</Label>
              <Input value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} disabled={!isEditable} />
            </div>
            <div>
              <Label>Ciutat</Label>
              <Input value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))} disabled={!isEditable} />
            </div>
            <div>
              <Label>Email de contacte</Label>
              <Input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} disabled={!isEditable} />
            </div>
            <div className="sm:col-span-2">
              <Label>Persona de contacte / Propietari</Label>
              <Input value={form.owner_email} onChange={e => setForm(f => ({ ...f, owner_email: e.target.value }))} disabled={!isEditable} placeholder="Email del responsable" />
            </div>
          </div>

          {/* Dades fiscals */}
          <div className="border-t border-gray-100 pt-4 mt-2">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Dades fiscals (Registro de Jornada)</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <Label>Raó social / Nom empresa</Label>
                <Input value={form.business_name} onChange={e => setForm(f => ({ ...f, business_name: e.target.value }))} disabled={!isEditable} placeholder="Ex: PERALES PEIRI VICTOR" />
              </div>
              <div>
                <Label>CIF / DNI</Label>
                <Input value={form.cif} onChange={e => setForm(f => ({ ...f, cif: e.target.value }))} disabled={!isEditable} placeholder="Ex: 39875073L" />
              </div>
              <div className="flex gap-2">
                <div className="w-20">
                  <Label>CCC Prov.</Label>
                  <Input value={form.ccc_provincia} onChange={e => setForm(f => ({ ...f, ccc_provincia: e.target.value }))} disabled={!isEditable} placeholder="43" />
                </div>
                <div className="flex-1">
                  <Label>CCC Número</Label>
                  <Input value={form.ccc_numero} onChange={e => setForm(f => ({ ...f, ccc_numero: e.target.value }))} disabled={!isEditable} placeholder="106910134" />
                </div>
              </div>
            </div>
          </div>

          {isEditable && (
            <Button onClick={handleSave} disabled={saving} className="bg-[#7B2D3F] hover:bg-[#5B1D2F] gap-2">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {saved ? "Guardat ✓" : "Guardar canvis"}
            </Button>
          )}
        </div>
      </Card>

      {/* Modules enabled */}
      <Card className="p-5 border-0 shadow-sm">
        <h3 className="font-semibold text-gray-900 mb-3">Mòduls inclosos al teu pla</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {Object.entries(FEATURE_LABELS).map(([key, label]) => {
            const included = features.includes(key);
            return (
              <div key={key} className={`flex items-center gap-2 text-sm px-3 py-2 rounded-xl ${included ? "bg-emerald-50 text-emerald-700" : "bg-gray-50 text-gray-400"}`}>
                <CheckCircle2 className={`w-4 h-4 flex-shrink-0 ${included ? "text-emerald-500" : "text-gray-300"}`} />
                {label}
              </div>
            );
          })}
        </div>
        {plan !== "premium" && (
          <p className="text-xs text-gray-400 mt-3">
            Contacta amb AEH Reus per actualitzar el teu pla i accedir a més funcionalitats.
          </p>
        )}
      </Card>
    </div>
  );
}